import { RessourcePathHelper, ConfigService } from '../../..';

/**
 * The service that implements the functions related to BOT server messages containing HTML tags that need to
 * be stardadized to fit into Widget interface. 
 */
export class MessagesNormalizationService {


    /**
     * Standardize HTMLiFrameElements.
     * 
     * @param iframeElems - The string representing HTML content that contains iframe tags
     */
     public static standardizeHTMLiframeElements(iframeElems: string) : string {
        // find all iframes that have position absolute
        const tmpArray: RegExpMatchArray | null = iframeElems.match(/<iframe [^>]+position\s*:\s*absolute/g);
        if (tmpArray !== null){
            // iframePosAbsolute  looks like <iframe frameborder="0" width="464" height="470" style="position: absolute;
            tmpArray.forEach((iframePosAbsolute: string)  => { 
                // in iframePosRelative , set position to relative
                const iframePosRelative  = iframePosAbsolute.replace(/position\s*:\s*absolute/g, 'position: relative'); 
                // replace iframePosAbsolute  with iframePosRelative
                iframeElems = iframeElems.replace(iframePosAbsolute , iframePosRelative ); 
            });
        }
        iframeElems = iframeElems.replace(/ :/g, '&nbsp;:');
        return iframeElems;
    }

    /**
     * Standardize HTMLImageElements providing the expected eventHandler definition on click, the style and / or classes definition.
     * 
     * @param imgElems - The string representing HTML content that contains img tags
     */
    public static standardizeHTMLImageElements(imgElems: string) : string {
        imgElems = imgElems.replace(/<img/g, '<img style="cursor: pointer; max-width: 100%;"')
        .replace(/ :/g, '&nbsp;:');
        return imgElems;
    }

    /**
     * Normalize HTMLImageElements providing the absolute path of the ressource and a normalized identifier.
     * 
     * @param imgElem - the HTMLImageElement to normalize
     * @param imgPathRoot - The absolute Root URL where the image is hosted.
     */
    public static normalizeHTMLImageElementPath(imgElem: HTMLImageElement, imgPathRoot: string) : HTMLImageElement {
        let newsrc: string = '';
        try {
            const localPathRootWithPort: string = `${document.location.protocol}//${document.location.hostname}:${document.location.port}`;
            const localPathRootWithoutPort: string = `${document.location.protocol}//${document.location.hostname}`;
            let imgURL: string = imgElem.src;
            if (imgURL.indexOf(localPathRootWithPort) !== -1) {
                imgURL = imgURL.replace(localPathRootWithPort, '');   
            }
            if (imgURL.indexOf(localPathRootWithoutPort) !== -1) {
                imgURL = imgURL.replace(localPathRootWithoutPort, '');            
            }
            newsrc = RessourcePathHelper.getAbsolutePath(imgPathRoot, imgURL);
        } finally {
            if (newsrc) {
                imgElem.src = newsrc;
            }
        }
        return imgElem;
    }

    /**
     * Normalize HTMLImageElements providing an unique ID
     * 
     * @param imgElem - the HTMLImageElement to normalize
     * @param conversationSessionInfo - the conversation informations as watermark value, used to build the ID | We can know to which answer images are linked
     * @param imgNumber - the image's number, used to build the ID | We can know which image is particulary used
     * @param imgElemsLength - the total number of images, used to build the ID | We can know how many images are linked to an answer
     */
    public static normalizeHTMLImageElementID(imgElem: HTMLImageElement, 
                                              conversationSessionInfo: any, 
                                              imgNumber: number, 
                                              imgElemsLength: number) : HTMLImageElement {
        if(!imgElem['id'] || imgElem['id'].indexOf('img-') === -1) {
            // An ID looks like this : 'img-1-0-1'. 
            imgElem['id'] = "img-" + (conversationSessionInfo ? conversationSessionInfo.nextMsgWatermark : "NaN") + "-" + imgNumber + "-" + imgElemsLength;
        }
        return imgElem;
    }

    /**
     * Standardize HTMLAnchorElements providing the style and / or classes definition.
     * 
     * @param anchorElems - The string representing HTML content that contains anchor tags
     * @param configTextColor - The configured color of the anchor elements
     */
    public static standardizeHTMLAnchorElements(anchorElems: string, configTextColor: string): string {
        // Attempt to match the first URL in the anchor elements string
        const urlMatch = anchorElems.match(/href="([^"]+)"/);
        let url = "";
        if (urlMatch) {
            url = urlMatch[1];
        }
        
        let hostname;
        let fileName;
        try {
            // Attempt to parse the URL to extract the hostname
            hostname = new URL(url).hostname;
            // Attempt to parse the URL to extract the pathname and filename
            const parsedUrl = new URL(url);
            const pathname = parsedUrl.pathname;
            fileName = pathname.split('/').pop();
        } catch (e) {
            // If parsing fails, check if the URL starts with 'http' to label as 'invalid site', otherwise use the original URL
            hostname = url.startsWith('http') ? 'site invalide' : url;
        }
        
        const externalWebSiteText = ConfigService.getExternalWebSiteQuoteButtonTitle() + " " + hostname + ' (' + ConfigService.getNewWindowButtonLabel() + ")";
        const webSiteText = "Source : " + ConfigService.getWebSiteQuoteButtonTitle() + " " + hostname + ' (' + ConfigService.getNewWindowButtonLabel() + ")";
        const docText = "Source : " + ConfigService.getFileQuoteButtonTitle() + " " + fileName + ' (' + ConfigService.getDownloadButtonLabel() + ")";
        
        // If the anchor elements string does not include 'quote-link', modify the anchor tags
        if (!anchorElems.includes('quote-link')) {
            // Replace the opening <a tag with customized attributes including target, style, class, title, and aria-label
            anchorElems = anchorElems.replace(/<a/g, '<a target="_blanc" style="color: '+ configTextColor +';" class="str-bot-answer-box-link" title="' + externalWebSiteText + '" aria-label="' + externalWebSiteText + '"');
        }
        else {
            // If the anchor elements string includes 'quote-link', replace the opening <a tag with tabindex attribute and add image
            anchorElems = anchorElems
                            .replace(/#website-link#/g, ' <img class="quote-image-size" src="' + ConfigService.getLinkButtonImage() + '" alt="' + webSiteText + '" />')
                            .replace(/#doc-link#/g, '<img class="quote-image-size" src="' + ConfigService.getDocButtonImage() + '" alt="' + docText + '" />');
        }

        anchorElems = anchorElems.replace(/ :/g, '&nbsp;:');
        return anchorElems;
    }
    
    /**
     * Normalize HTMLAnchorElement providing the CSS class name.
     * 
     * @param anchorElem - the HTMLAnchorElement to normalize
     */
    public static normalizeHTMLAnchorElementPath(anchorElem: HTMLAnchorElement) : HTMLAnchorElement {
        anchorElem.classList.add('str-bot-answer-box-link');
        return anchorElem;
    }

    /**
     * Standardize HTMLTableElements providing the expected eventHandler definition on click, the style and / or classes definition.
     * Unused
     * @param tableElement - The string representing HTML content that contains table tag
     */
    public static standardizeHTMLTableElements(tableElement: string) : string {
        tableElement = tableElement.replace(/<table/g, '<table onClick="javascript:window.onMessageBoxTableSelected([this], 0);"')
                           .replace(/ :/g, '&nbsp;:');

        return tableElement;
    }

    /**
     * Normalize HTMLTableElements providing an unique ID
     * 
     * @param tableElement - the HTMLTableElement to normalize
     * @param conversationSessionInfo - the conversation informations as watermark value, used to build the ID | We can know to which answer images are linked
     * @param tableNumber - the image's number, used to build the ID | We can know which image is particulary used
     * @param tableElemsLength - the total number of images, used to build the ID | We can know how many images are linked to an answer
     */
    public static normalizeHTMLTableElementID(tableElement: HTMLTableElement, 
                                              conversationSessionInfo: any, 
                                              tableNumber: number, 
                                              tableElemsLength: number) : HTMLTableElement {


        if(!tableElement['id'] || tableElement['id'].indexOf('table-') === -1) {
            // An ID looks like this : 'img-1-0-1'. 
            tableElement['id'] = "table-" + (conversationSessionInfo ? conversationSessionInfo.nextMsgWatermark : "NaN") + "-" + tableNumber + "-" + tableElemsLength;
        }
        return tableElement;
    }

    /**
     * Standardize HTML ImageElements in table by providing the expected eventHandler definition on click, the style and / or classes definition.
     * 
     * @param imgElem - the HTMLImageElement to normalizer
     */
    public static normalizeHTMLTableImageElement(imgElem: HTMLImageElement) : HTMLImageElement {

        // Test if the images are in a table
        if(imgElem.parentElement && imgElem.parentElement.tagName == 'TD') {
            imgElem.parentElement.style.whiteSpace = "normal"; 
        }

        return imgElem;
    }

    /**
     * Normalize HTML paragraph end tag's break line for message formatting
     * 
     * @param paragraphNodes 
     */
    public static normalizeHTMLBreakLineForParagraphElement(paragraphNodes: NodeListOf<HTMLElement>) {
        paragraphNodes.forEach((pElem: HTMLElement, key: number, paragraphNodes: NodeListOf<HTMLElement>) => {
            // For message formatting concerns, the last paragraph's break line is ignored
            if(key !== paragraphNodes.length - 1 && pElem.parentNode !== null) {
                // It's fine to replace br tags by a div with CSS rules on it if needed
                const newNode: Node = document.createElement('br');
                // We can't use pElem.after() because it isn't supported by IE11
                pElem.parentNode.insertBefore(newNode, pElem.nextSibling);
            }
        })
    }

}
